// SPDX-License-Identifier: AGPL-3.0-or-later
import type { BudgetLine, Cents, IsoDate, RebalanceMode, SplitRule } from "@almanac/domain";
import { MAIN_CATEGORIES, type CustomCategory, type Profile } from "./categories.js";

export interface Account {
  id: string;
  name: string;
  /** Import profile id, e.g. "rbc", when the account came from a bank CSV. */
  bank: string | null;
}

export interface Transaction {
  id: string;
  accountId: string;
  date: IsoDate;
  /** Negative is money out. */
  amount: Cents;
  description: string;
  category: string | null;
  fingerprint: string;
}

/**
 * Transactions are stored one encrypted record per account per month, so the
 * server sees how many account-months exist rather than how many purchases.
 */
export interface MonthBundle {
  accountId: string;
  month: string; // YYYY-MM
  transactions: Transaction[];
}

export interface BudgetPlan {
  /** Monthly pool shared by every slider. */
  pool: Cents;
  mode: RebalanceMode;
  /** One line per main category. */
  lines: BudgetLine[];
  /**
   * How a category's amount is split among its sub-categories, keyed by the
   * category. Each split holds a line per child plus one for the category
   * itself ("everything else"), and always adds up to the category's amount.
   */
  splits: Record<string, BudgetLine[]>;
  /**
   * What to set aside each month for large purchases. Null (the default)
   * follows what the save-up goals need; an amount caps it, and the goals
   * share it in priority order.
   */
  purchaseLimit?: Cents | null;
}

export type Repeat = "none" | "weekly" | "biweekly" | "monthly" | "yearly";
export const REPEATS: readonly Repeat[] = ["none", "weekly", "biweekly", "monthly", "yearly"];

/** Something on the calendar: a bill, a birthday, a renewal. One encrypted record each. */
export interface CalendarEvent {
  id: string;
  title: string;
  /** First (or only) occurrence. */
  date: IsoDate;
  repeat: Repeat;
  /** Last possible occurrence, inclusive. */
  until: IsoDate | null;
  /** Expected cost, if any. Positive is money out. */
  amount: Cents | null;
  category: string | null;
  notes: string;
  /** Kept out of the calendar feed even when the feed is on. */
  hidden: boolean;
  /** Set on loan payments that a financed large purchase keeps up to date. */
  purchaseId?: string;
}

/** Something big to buy: saved up for by a date, or financed from that date. One encrypted record each. */
export interface Purchase {
  id: string;
  name: string;
  price: Cents;
  /** When to buy it. */
  targetDate: IsoDate;
  mode: "save" | "finance";
  /** Lower comes first when money is short. */
  priority: number;
  /** Saving up: put aside so far. Financing: the down payment. */
  saved: Cents;
  /** Financing: nominal annual rate, e.g. 0.0699. */
  rate: number | null;
  /** Financing: loan length in months. */
  termMonths: number | null;
  /** Financing: where the payments count in the budget. */
  category: string | null;
  /** Financing: the monthly payment event this purchase keeps in step. */
  eventId: string | null;
  /** Set once it is bought (saving up). Bought purchases stop asking for money. */
  boughtOn: IsoDate | null;
}

/** The member of every group who is the person using the app. */
export const ME = "me";

export interface GroupMember {
  id: string;
  name: string;
}

/** Something one member paid for the group. */
export interface GroupExpense {
  id: string;
  date: IsoDate;
  description: string;
  amount: Cents;
  paidBy: string;
  split: SplitRule;
  /** The imported transaction that paid it, when you paid. Others' shares then leave your own spending. */
  txId: string | null;
}

/** A bill that repeats, such as a shared phone plan. Each due date counts as an expense. */
export interface RecurringExpense {
  id: string;
  description: string;
  amount: Cents;
  paidBy: string;
  split: SplitRule;
  start: IsoDate;
  repeat: Exclude<Repeat, "none">;
  until: IsoDate | null;
}

/** Money one member gave another to settle up. */
export interface GroupPayment {
  id: string;
  date: IsoDate;
  from: string;
  to: string;
  amount: Cents;
  /** The incoming transfer it came from, when it was matched to one. */
  txId: string | null;
}

/** People who share costs, such as a family phone plan. One encrypted record each. */
export interface Group {
  id: string;
  name: string;
  /** Always includes ME. */
  members: GroupMember[];
  expenses: GroupExpense[];
  recurring: RecurringExpense[];
  payments: GroupPayment[];
  /** Incoming transfers declined as repayments, so they are not offered again. */
  declined: string[];
}

/**
 * Settings for the private calendar feed, kept encrypted. The token is kept
 * here because the server stores only its hash and cannot show the address again.
 */
export interface FeedPrefs {
  token: string | null;
  /** What each feed entry shows: the title only (default) or the title and amount. */
  detail: "title" | "amount";
}

/** What phone pushes may say. The server only ever sees what this allows. */
export type PushDetail = "generic" | "titles" | "amounts";

/** Notification choices, kept encrypted. Phone settings live on the server, which sends the pushes. */
export interface NotifyPrefs {
  /** Which kinds of notification show in the app (and, for budgets, on the phone). */
  types: { bills: boolean; budgets: boolean; goals: boolean };
  /** Notifications the user dismissed, by id. Pruned to those still current. */
  dismissed: string[];
  /** Null while phone notifications are off. */
  push: { detail: PushDetail } | null;
}

export interface Snapshot {
  accounts: Account[];
  months: MonthBundle[];
  plan: BudgetPlan | null;
  /** payeeKey → category, learned from the user's corrections. */
  learned: Record<string, string>;
  events: CalendarEvent[];
  feed: FeedPrefs;
  /** Setup survey answers; null until the survey is answered or skipped. */
  profile: Profile | null;
  custom: CustomCategory[];
  notify: NotifyPrefs;
  purchases: Purchase[];
  groups: Group[];
}

export const DEFAULT_FEED: FeedPrefs = { token: null, detail: "title" };
export const DEFAULT_NOTIFY: NotifyPrefs = { types: { bills: true, budgets: true, goals: true }, dismissed: [], push: null };

/** An empty snapshot, for a new user. */
export function emptySnapshot(): Snapshot {
  return { accounts: [], months: [], plan: null, learned: {}, events: [], feed: DEFAULT_FEED, profile: null, custom: [], notify: DEFAULT_NOTIFY, purchases: [], groups: [] };
}

export { NOT_SPENDING } from "./categories.js";

export function defaultPlan(pool: Cents = 500_000): BudgetPlan {
  const weights: Record<string, number> = {
    housing: 44,
    transport: 9,
    groceries: 14,
    dining: 5,
    health: 3,
    shopping: 5,
    household: 3,
    entertainment: 3,
    other: 0,
    savings: 14,
    purchases: 0,
  };
  const lines = MAIN_CATEGORIES.map((id) => ({ id, amount: Math.round((pool * (weights[id] ?? 0)) / 100), locked: false }));
  const drift = pool - lines.reduce((s, l) => s + l.amount, 0);
  lines.find((l) => l.id === "savings")!.amount += drift;
  return { pool, mode: "equal", lines: lines.map((l) => (l.id === "purchases" ? { ...l, locked: true } : l)), splits: {} };
}

export function uuid(): string {
  return crypto.randomUUID();
}
